import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min'
import queryString from 'query-string'

import { SystemMessageContext } from '../../../context/SystemMessageContext'
import useCallApi from '../../../hooks/useCallApi'
import Loading from '../../../templates/UI/Loading/Loading'
import Dollars from '../../../templates/Dollars/Dollars'
import Number from '../../../templates/Number/Number'
import Advertiser from '../../../templates/Advertiser/Advertiser'

import { Modal } from 'react-bootstrap'

// Year is hardcoded for now, Update this when we need a dynamic year selector
const REPORT_YEAR = 2026

function todayLocalYYYYMMDD() {
    const d = new Date()
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
}

function parseBool(value, defaultValue = false) {
    if (value === true) return true
    if (value === false) return false
    if (typeof value !== 'string') return defaultValue
    if (value.toLowerCase() === 'true') return true
    if (value.toLowerCase() === 'false') return false
    return defaultValue
}

export default function WeeklyPaymentsReport() {
    document.title = 'Weekly Payments'

    const { showSystemMessage } = useContext(SystemMessageContext)
    const history = useHistory()
    const location = useLocation()
    const urlParams = queryString.parse(location.search)

    const [periodStartDate, setPeriodStartDate] = useState(urlParams.startDate || '')

    const [payable, setPayable] = useState(parseBool(urlParams.payable, true))
    const [waiting, setWaiting] = useState(parseBool(urlParams.waiting, true))

    /** Table rows follow URL; dropdown is draft until Refresh Report. */
    const tableStartDate = urlParams.startDate || ''

    const [modalOpen, setModalOpen] = useState(false)
    const [modalLoading, setModalLoading] = useState(false)
    const [modalStatus, setModalStatus] = useState('P') // P | W
    const [modalPeriod, setModalPeriod] = useState(null)
    const [modalAdvertisers, setModalAdvertisers] = useState([])

    useEffect(() => {
        setPeriodStartDate(queryString.parse(location.search).startDate || '')
    }, [location.search])

    const periodsUrl = useMemo(() => {
        let url = `reports/custom/weeklyPaymentsReport?year=${REPORT_YEAR}`
        if (payable) url += `&payable=true`
        if (waiting) url += `&waiting=true`
        return url
    }, [payable, waiting])

    const { data, dataLoaded } = useCallApi(periodsUrl, 'GET', {}, [location], showSystemMessage)

    const periods = (dataLoaded && data && data.report) ? data.report : []

    /** Periods whose week has started (Sunday start ≤ today); excludes upcoming weeks */
    const periodsEligible = useMemo(() => {
        const today = todayLocalYYYYMMDD()
        return periods.filter((p) => p.startDate <= today)
    }, [periods])

    useEffect(() => {
        if (!periodStartDate || !dataLoaded) return
        if (!periodsEligible.some((p) => p.startDate === periodStartDate)) {
            setPeriodStartDate('')
        }
    }, [dataLoaded, periodsEligible, periodStartDate])

    const selectedPeriod = useMemo(() => {
        if (!tableStartDate) return null
        return periodsEligible.find((p) => p.startDate === tableStartDate) || null
    }, [periodsEligible, tableStartDate])

    const visiblePeriods = useMemo(() => {
        if (!tableStartDate) return periodsEligible
        return selectedPeriod ? [selectedPeriod] : periodsEligible
    }, [periodsEligible, tableStartDate, selectedPeriod])

    const submitHandler = () => {
        let url = `/reports/custom/weeklyPaymentsReport?year=${REPORT_YEAR}`
        if (periodStartDate) url += `&startDate=${periodStartDate}`
        if (payable) url += `&payable=true`
        if (waiting) url += `&waiting=true`
        history.push(url)
    }

    const openAdvertisersModal = async (period, status) => {
        if (!period) return

        setModalOpen(true)
        setModalLoading(true)
        setModalStatus(status)
        setModalPeriod(period)
        setModalAdvertisers([])

        try {
            const startDate = period.startDate
            const endDateExclusive = period.endDateExclusive
            const mbToken = !localStorage.getItem('adm-auth-token') ? '' : localStorage.getItem('adm-auth-token')

            const fetchUrl = `${process.env.REACT_APP_API_URL}/reports/custom/weeklyPaymentsReport/merchants?startDate=${startDate}&endDateExclusive=${endDateExclusive}&status=${status}`

            const res = await fetch(fetchUrl, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'x-app-token': mbToken,
                },
            })
            const result = await res.json()

            if (result && result.success) {
                setModalAdvertisers(result.report || [])
            } else {
                showSystemMessage('error', result?.errors?.message || 'There was an error fetching merchant breakdown.')
            }
        } catch (err) {
            showSystemMessage('error', 'Connection error fetching merchant breakdown.')
        } finally {
            setModalLoading(false)
        }
    }

    const closeModal = () => {
        setModalOpen(false)
        setModalPeriod(null)
        setModalAdvertisers([])
    }

    const renderFilters = () => {
        return (
            <div className="d-flex align-items-center p-3">
                <div className="mr-4 d-flex align-items-center">
                    <h1 className="mb-h1-dark">Weekly Payments</h1>
                </div>

                <div className="d-flex align-items-center">
                    <div>
                        <div className="form-control form-control-sm bg-light">{REPORT_YEAR}</div>
                    </div>

                    <div className="ml-2">
                        <select
                            className="form-control form-control-sm"
                            value={periodStartDate}
                            onChange={(e) => setPeriodStartDate(e.target.value)}
                        >
                            <option value="">All periods</option>
                            {periodsEligible.map((p) => (
                                <option key={p.startDate} value={p.startDate}>
                                    {p.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="ml-2">
                        <button className="btn btn-primary btn-sm" onClick={() => submitHandler()}>
                            Refresh Report
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    const renderTable = () => {
        return (
            <table className="table table-sm table-striped table-card table-bordered table-hover mb-0">
                <thead>
                    <tr>
                        <th>Period</th>
                        <th className="text-right">Payable</th>
                        <th className="text-right">Waiting</th>
                    </tr>
                </thead>
                <tbody>
                    {visiblePeriods.map((p) => (
                        <tr key={p.startDate}>
                            <td className="text-left">
                                {p.label}
                            </td>
                            <td className="text-right">
                                <button
                                    className="btn btn-link p-0"
                                    disabled={(p.payable_amount || 0) === 0}
                                    onClick={() => openAdvertisersModal(p, 'P')}
                                >
                                    <Dollars value={p.payable_amount || 0} />
                                </button>
                            </td>
                            <td className="text-right">
                                <button
                                    className="btn btn-link p-0"
                                    disabled={(p.waiting_amount || 0) === 0}
                                    onClick={() => openAdvertisersModal(p, 'W')}
                                >
                                    <Dollars value={p.waiting_amount || 0} />
                                </button>
                            </td>
                        </tr>
                    ))}
                    {visiblePeriods.length === 0 && (
                        <tr>
                            <td colSpan={3} className="p-3">No periods.</td>
                        </tr>
                    )}
                </tbody>
            </table>
        )
    }

    const renderModal = () => {
        const title = modalPeriod
            ? `Advertiser breakdown (${modalStatus === 'P' ? 'Payable' : 'Waiting'}) — ${modalPeriod.label}`
            : 'Advertiser breakdown'

        return (
            <Modal show={modalOpen} onHide={closeModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{title}</Modal.Title>
                </Modal.Header>
                <div className="p-3">
                    {modalLoading ? (
                        <Loading />
                    ) : (
                        <table className="table table-striped table-bordered table-hover mb-0">
                            <thead>
                                <tr>
                                    <th>Advertiser</th>
                                    <th className="text-right">Amount</th>
                                    <th className="text-right">Leads</th>
                                </tr>
                            </thead>
                            <tbody>
                                {modalAdvertisers.map((m) => (
                                    <tr key={m.merchant_id}>
                                        <td>
                                            <Advertiser id={m.merchant_id} name={m.merchant_name || ''} status="" />
                                        </td>
                                        <td className="text-right"><Dollars value={m.affiliate_amount || 0} /></td>
                                        <td className="text-right"><Number value={m.lead_count || 0} /></td>
                                    </tr>
                                ))}
                                {modalAdvertisers.length === 0 && (
                                    <tr>
                                        <td colSpan={3} className="p-2">No data.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </Modal>
        )
    }

    return (
        <React.Fragment>
            <div className="row">
                <div className="col-xl-6">
                    <div className="page-header d-flex align-items-center">
                        <span className="mb-h1">Reports</span>
                    </div>
                    <div className="card pb-0">
                        {renderFilters()}
                        <div>
                            {dataLoaded ? renderTable() : <Loading />}
                        </div>
                    </div>
                </div>
            </div>
            {renderModal()}
        </React.Fragment>
    )
}

